import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Coins, Leaf, Users, Handshake, Building2, Globe, AlertTriangle,
  Sliders, Clock, ChevronRight, TrendingUp, Award, ShieldCheck, Landmark,
  Pickaxe, Compass, ScrollText, Sparkles,
} from "lucide-react";

const GOLD = "#C99A2E"; const PURPLE = "#7B2FBE"; const GREEN = "#6BB88A";
const BLUE = "#4FA3D1"; const TEAL = "#2E7D6B"; const CRIT = "#E67E7E";
const WARN = "#C99A2E"; const RES = "#5A7A8A";

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const M = ({ label, value, accent = GOLD, hint }) => (
  <div className="rounded-sm border px-3 py-2" style={{ borderColor: accent + "30", background: "rgba(7,27,43,0.55)" }}>
    <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#7C93A5]">{label}</div>
    <div className="font-display font-bold mt-0.5 text-[18px] break-words" style={{ color: accent }}>{value}</div>
    {hint && <div className="text-[10px] text-[#7C93A5] font-mono mt-0.5">{hint}</div>}
  </div>
);

// Benefits realisation across 7 stakeholder groups
const BENEFITS = [
  { group: "Government value",       accent: GOLD,   metrics: [["Revenue collected", "USD 1.28B"], ["Revenue protected", "USD 342M"], ["Officer productivity", "+29%"]] },
  { group: "Citizen value",          accent: GREEN,  metrics: [["Service turnaround", "-42%"], ["Portal availability", "94.6%"], ["Citizen satisfaction", "+19%"]] },
  { group: "Environmental value",    accent: TEAL,   metrics: [["Natural capital protected", "1.46M ha"], ["CO₂ savings", "8,412 t/yr"], ["Environmental compliance", "+24%"]] },
  { group: "Economic value",         accent: GOLD,   metrics: [["Investment enabled", "USD 3.4B"], ["Resource GDP share", "16.4%"], ["Export readiness", "+31%"]] },
  { group: "Investor value",         accent: GREEN,  metrics: [["Time-to-licence", "-38%"], ["Transparency index", "82.4"], ["Investment confidence", "+31%"]] },
  { group: "Institutional value",    accent: PURPLE, metrics: [["Paper elimination", "-68%"], ["Workflow digitisation", "84%"], ["Digital Trust score", "92"]] },
  { group: "International reporting",accent: BLUE,   metrics: [["Frameworks aligned", "9"], ["Report readiness", "+46%"], ["Audit trail coverage", "100%"]] },
];

// Cost of inaction (annualised)
const INACTION = [
  { label: "Revenue leakage",                  cost: "USD 218M/yr",  accent: CRIT },
  { label: "Administrative delay cost",         cost: "USD 84M/yr",   accent: CRIT },
  { label: "Paper-based operational cost",      cost: "USD 42M/yr",   accent: WARN },
  { label: "Duplicate inspections",             cost: "USD 18M/yr",   accent: WARN },
  { label: "Compliance risk exposure",          cost: "USD 96M/yr",   accent: CRIT },
  { label: "Environmental degradation",         cost: "USD 148M/yr",  accent: CRIT },
  { label: "Investment delay opportunity cost", cost: "USD 620M/yr",  accent: CRIT },
];

// Deployment roadmap paths
const ROADMAP = [
  { path: "Mine360 only",         nodes: ["Mine360"] },
  { path: "Land360 only",         nodes: ["Land360"] },
  { path: "Survey360 only",       nodes: ["Survey360"] },
  { path: "Environment360 only",  nodes: ["Environment360"] },
  { path: "Combined deployment",  nodes: ["Land360", "Mine360", "Survey360", "Environment360"] },
  { path: "GSROS executive layer",nodes: ["GSROS"] },
  { path: "Whole-of-government",  nodes: ["Land360", "Mine360", "Survey360", "Environment360", "GSROS"] },
];

// Transformation timeline
const TIMELINE = [
  { year: "2024 Q1", event: "Cadastre360 foundational rollout — Land + Mining + Survey",           accent: GOLD },
  { year: "2024 Q3", event: "Public Investor Portal · Digital Trust · QR-verified certificates",    accent: PURPLE },
  { year: "2025 Q1", event: "Sovereign Intelligence Centre (Phase VII) — national situation view",  accent: PURPLE },
  { year: "2025 Q2", event: "GSROS™ executive orchestration + cabinet briefing engine",             accent: PURPLE },
  { year: "2025 Q4", event: "Executive experience transformation — command palette, presentation",  accent: BLUE },
  { year: "2026 Q1", event: "Environmental Governance™ — 4th sovereign pillar",                     accent: TEAL },
  { year: "2026 Q1", event: "THOM360 architecture + Sovereign Platform Cockpit",                    accent: GOLD },
  { year: "2026 Q1", event: "National Value Centre™ — value realisation across seven stakeholders", accent: GOLD },
];

// Development partner frameworks (configurable)
const FRAMEWORKS = [
  { code: "NDS-1",  name: "National Development Strategy",       score: 84 },
  { code: "V2030",  name: "Vision 2030",                          score: 78 },
  { code: "SDG",    name: "UN Sustainable Development Goals",     score: 76 },
  { code: "AU",     name: "AU Agenda 2063",                       score: 74 },
  { code: "SADC",   name: "SADC Regional Programme",              score: 80 },
  { code: "UNFCCC", name: "UN Framework Convention Climate",      score: 72 },
  { code: "CBD",    name: "Convention on Biological Diversity",   score: 76 },
  { code: "EITI",   name: "Extractive Industries Transparency",   score: 88 },
];

// Investor confidence indicators
const INVESTOR = [
  { label: "Transparency",           score: 82 },
  { label: "Permitting efficiency",  score: 76 },
  { label: "Compliance performance", score: 84 },
  { label: "Governance maturity",    score: 81 },
  { label: "Data availability",      score: 88 },
  { label: "Regulatory clarity",     score: 78 },
];

// Citizen impact indicators
const CITIZEN = [
  { label: "Service delivery time",   value: "-42%",  accent: GREEN },
  { label: "Application turnaround", value: "-38%",  accent: GREEN },
  { label: "Transparency score",     value: "82.4",  accent: BLUE },
  { label: "Digital access reach",   value: "+64%",  accent: GREEN },
  { label: "Portal availability",    value: "94.6%", accent: GREEN },
  { label: "Complaint resolution",   value: "-31%",  accent: GREEN },
];

// Executive decision playbooks
const PLAYBOOKS = [
  { audience: "Cabinet",            action: "Approve national titling acceleration + establish quarterly performance review anchored to Sovereign Performance Index",             icon: Building2, to: "/app/gsros" },
  { audience: "Treasury",           action: "Adopt digital-first collections task-force targeting USD 342M outstanding revenue and USD 218M annual leakage",                       icon: Coins,     to: "/app/revenue" },
  { audience: "Minister · Mines",   action: "Convene Technical Review Committee on mineral-right conflicts within 72 hours; fast-track lithium beneficiation SEZ",                icon: Award,     to: "/app/mining" },
  { audience: "Minister · Lands",   action: "Accelerate national parcel titling and cross-reference with Environmental Governance protected-area intersections",                  icon: Landmark,  to: "/app/land" },
  { audience: "Minister · Env.",    action: "Publish quarterly Environmental Situation Room report + prioritise Manyame River pollution containment",                             icon: Leaf,      to: "/app/environment" },
  { audience: "Investors",          action: "Publish Investment Prospectus with 8 strategic minerals + 4 beneficiation SEZs + interactive GIS opportunity map",                    icon: Handshake, to: "/app/gsros" },
  { audience: "Development partner",action: "Align with EITI · SDG · AU · SADC quarterly reports; expose signed evidence packs via International Reporting Centre",                icon: Globe,     to: "/app/gsros" },
];

const Bar = ({ label, score, accent = PURPLE, right }) => (
  <div className="flex items-center gap-2 py-1 text-[12px]">
    <div className="w-56 truncate text-[#F6F8FA]">{label}</div>
    <div className="flex-1 h-1.5 rounded-sm bg-white/5 relative">
      <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${score}%`, background: accent }} />
    </div>
    <div className="w-12 text-right font-mono text-[11px] text-[#F6F8FA]">{score}</div>
    {right && <div className="w-16 text-right font-mono text-[10px] text-[#7C93A5]">{right}</div>}
  </div>
);

// ROI Simulator
const RoiSimulator = () => {
  const [turnaround, setTurnaround] = useState(35);   // % improvement in approval turnaround
  const [inspection, setInspection] = useState(30);   // % gain in inspection productivity
  const [adoption, setAdoption] = useState(75);       // % digital adoption

  const projected = useMemo(() => {
    const baseRevenue = 1_285;             // USD m baseline
    const revenueDelta = baseRevenue * (turnaround * 0.004 + inspection * 0.002 + adoption * 0.006);
    const hoursSaved = 218_400 * (turnaround / 100 + adoption / 100) / 2;
    const paperSaved = 12_400_000 * (adoption / 100);
    const co2 = 8_412 * (adoption / 100);
    const invest = 3_400 * (1 + (turnaround / 100) * 0.3 + (adoption / 100) * 0.4);
    return {
      revenue: revenueDelta.toFixed(1),
      hours: Math.round(hoursSaved).toLocaleString(),
      paper: (paperSaved / 1_000_000).toFixed(1),
      co2: Math.round(co2).toLocaleString(),
      invest: invest.toFixed(0),
    };
  }, [turnaround, inspection, adoption]);

  const Slider = ({ label, value, setValue, hint, testId }) => (
    <div className="rounded-sm border border-white/10 p-3" style={{ background: "rgba(7,27,43,0.55)" }}>
      <div className="flex items-center gap-2">
        <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#7C93A5] flex-1">{label}</div>
        <div className="font-mono text-[13px] font-bold" style={{ color: GOLD }}>{value}%</div>
      </div>
      <input type="range" min={0} max={100} value={value} onChange={(e) => setValue(Number(e.target.value))}
             data-testid={testId} className="w-full mt-2 accent-[#C99A2E]" />
      {hint && <div className="text-[10px] text-[#7C93A5] font-mono mt-1">{hint}</div>}
    </div>
  );

  return (
    <Card title="Executive ROI Simulator™ — scenario projection (not historical)" accent={GOLD} testId="roi-simulator">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <Slider label="Approval turnaround improvement" value={turnaround} setValue={setTurnaround} hint="Baseline: 45 days · Best-in-class: 12 days" testId="roi-turnaround" />
        <Slider label="Inspection productivity gain"     value={inspection} setValue={setInspection} hint="Officer productivity uplift"                     testId="roi-inspection" />
        <Slider label="Digital adoption"                  value={adoption}   setValue={setAdoption}   hint="Users transacting digitally"                     testId="roi-adoption" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <M label="Projected revenue uplift"  value={`USD ${projected.revenue}M/yr`} accent={GOLD} />
        <M label="Officer hours saved / yr"  value={projected.hours}                accent={GREEN} />
        <M label="Paper eliminated / yr"     value={`${projected.paper}M pages`}    accent={GREEN} />
        <M label="CO₂ savings / yr"          value={`${projected.co2} t`}           accent={TEAL} />
        <M label="Investment enabled"        value={`USD ${projected.invest}M`}     accent={GREEN} />
      </div>
      <div className="mt-2 text-[10px] text-[#7C93A5] font-mono italic">
        Scenario projection — not a legal or audited financial statement. Consumes existing analytics only.
      </div>
    </Card>
  );
};

export const NationalTransformationCentre = () => {
  return (
    <div className="p-6 space-y-4" data-testid="page-transformation">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}25 100%)` }}>
        <Sparkles size={38} className="mt-1" style={{ color: GOLD }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GOLD }}>
            Cadastre360™ · Phase XIII-A · National Transformation Intelligence v1.13a
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Executive Decision Centre for national transformation
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl">
            Benefits realisation across seven stakeholder groups · executive cost of inaction ·
            phased implementation roadmap · ROI simulator · transformation timeline · development-partner
            alignment · investor and citizen confidence · executive decision playbooks. Presentation-only.
          </div>
        </div>
      </div>

      {/* Benefits realisation */}
      <Card title="National Benefits Realisation Dashboard™" accent={GOLD} testId="benefits-dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {BENEFITS.map((b) => (
            <div key={b.group} className="rounded-sm border p-2.5" style={{ borderColor: b.accent + "40", background: "rgba(7,27,43,0.5)" }}>
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 rounded-sm" style={{ background: b.accent }} />
                <div className="font-mono uppercase tracking-[0.16em] text-[10px]" style={{ color: b.accent }}>{b.group}</div>
              </div>
              <div className="mt-2 space-y-0.5">
                {b.metrics.map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <div className="flex-1 text-[#DCE3E8]">{k}</div>
                    <div className="font-mono font-bold" style={{ color: b.accent }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Cost of inaction + ROI */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Executive Cost of Inaction™" accent={CRIT} testId="cost-of-inaction">
          <div className="space-y-1">
            {INACTION.map((i) => (
              <div key={i.label} className="border-b border-white/5 pb-1 flex items-center gap-2 text-[12px]">
                <AlertTriangle size={10} style={{ color: i.accent }} />
                <div className="flex-1 text-[#F6F8FA]">{i.label}</div>
                <div className="font-mono font-bold" style={{ color: i.accent }}>{i.cost}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-[#7C93A5] font-mono italic">
            Scenario analysis. Consumes existing analytics only.
          </div>
        </Card>
        <div className="lg:col-span-2">
          <RoiSimulator />
        </div>
      </div>

      {/* Roadmap */}
      <Card title="Phased National Implementation Roadmap™" accent={PURPLE} testId="phased-roadmap">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {ROADMAP.map((r) => (
            <div key={r.path} className="rounded-sm border border-white/10 p-2.5" style={{ background: "rgba(7,27,43,0.5)" }}>
              <div className="font-mono uppercase tracking-[0.16em] text-[10px] text-[#C99A2E]">{r.path}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {r.nodes.map((n, i) => (
                  <React.Fragment key={n + i}>
                    <span className="rounded-sm px-2 py-0.5 text-[11px] font-mono" style={{ background: GOLD + "25", color: GOLD }}>{n}</span>
                    {i < r.nodes.length - 1 && <ChevronRight size={10} className="text-[#7C93A5]" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Timeline */}
      <Card title="National Transformation Timeline™" accent={BLUE} testId="transformation-timeline">
        <div className="relative border-l-2 pl-4 ml-1" style={{ borderColor: BLUE + "40" }}>
          {TIMELINE.map((t, i) => (
            <div key={t.year + i} className="pb-2 relative">
              <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full" style={{ background: t.accent }} />
              <div className="flex items-baseline gap-2">
                <div className="font-mono uppercase text-[10px] tracking-[0.14em]" style={{ color: t.accent }}>{t.year}</div>
                <div className="text-[12px] text-[#F6F8FA]">{t.event}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Development partner alignment + investor + citizen */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Development Partner Alignment™" accent={BLUE} testId="dev-partner-alignment">
          {FRAMEWORKS.map((f) => <Bar key={f.code} label={`${f.code} · ${f.name}`} score={f.score} accent={BLUE} />)}
          <div className="mt-2 text-[10px] text-[#7C93A5] font-mono italic">Configurable · no hard-coded country assumptions.</div>
        </Card>
        <Card title="Investment Confidence Dashboard™" accent={GREEN} testId="investor-confidence">
          {INVESTOR.map((i) => <Bar key={i.label} label={i.label} score={i.score} accent={GREEN} />)}
        </Card>
        <Card title="Citizen Impact Dashboard™" accent={GREEN} testId="citizen-impact">
          <div className="grid grid-cols-2 gap-2">
            {CITIZEN.map((c) => <M key={c.label} label={c.label} value={c.value} accent={c.accent} />)}
          </div>
        </Card>
      </div>

      {/* Decision playbooks */}
      <Card title="Executive Decision Playbooks™" accent={PURPLE} testId="decision-playbooks">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PLAYBOOKS.map((p) => (
            <Link key={p.audience} to={p.to}
                  data-testid={"playbook-" + p.audience.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
                  className="rounded-sm border p-2.5 flex items-start gap-3"
                  style={{ borderColor: PURPLE + "40", background: "rgba(7,27,43,0.5)" }}>
              <p.icon size={16} style={{ color: PURPLE }} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-mono uppercase tracking-[0.16em] text-[10px]" style={{ color: PURPLE }}>{p.audience}</div>
                <div className="text-[12px] text-[#F6F8FA] mt-0.5">{p.action}</div>
              </div>
              <ChevronRight size={11} className="text-[#7C93A5] mt-1" />
            </Link>
          ))}
        </div>
      </Card>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] flex flex-wrap items-center gap-3 pt-1">
        <span>Cadastre360™ · Phase XIII-A · Executive Decision Centre</span>
        <span>·</span>
        <span>Presentation-only · zero regression · consumes existing services</span>
      </div>
    </div>
  );
};

export default NationalTransformationCentre;
